import { Box, Paper, Typography, useTheme } from '@mui/material';
import { formatMetricValue } from '../../utils/formatMetricValue';
import { cardShellSx, getMetricDisplayName } from './rendererTypes';
import type { MetricRendererProps } from './rendererTypes';
import TileMetaActions from '../TileMetaActions';
import { tileMetaTitlePad } from '../../utils/tileMeta';
import TrendBadge from './TrendBadge';

function BenchmarkBar({
  value,
  rangeLabels,
}: {
  value: number;
  rangeLabels: [string, string];
}) {
  const theme = useTheme();
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <Box>
      <Box
        sx={{
          height: 8,
          borderRadius: 1,
          backgroundColor: theme.palette.action.hover,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${clamped}%`,
            height: '100%',
            backgroundColor: theme.palette.primary.main,
            borderRadius: 1,
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
          {rangeLabels[0]}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
          {rangeLabels[1]}
        </Typography>
      </Box>
    </Box>
  );
}

/** Always "Baseline N%" / "Best N%" from benchmark numbers. */
function resolveRangeLabels(
  best: number | undefined,
  baseline: number | undefined,
  format?: MetricRendererProps['config']['format'],
): [string, string] | null {
  if (best == null || baseline == null) return null;
  const valueFormat = format ?? 'percent';
  return [
    `Baseline ${formatMetricValue(baseline, valueFormat)}`,
    `Best ${formatMetricValue(best, valueFormat)}`,
  ];
}

/**
 * Fill toward the “good” end of baseline→best.
 * down (e.g. unsubscribe 0.04, baseline 30, best 5) → nearly full when at best.
 */
function resolveBenchmarkProgress(
  current: number,
  best: number | undefined,
  baseline: number | undefined,
  goodDirection?: 'up' | 'down',
): number {
  if (best == null || baseline == null || best === baseline) {
    return Math.min(100, Math.max(0, current));
  }

  if (goodDirection === 'down') {
    const range = baseline - best;
    if (range === 0) return 0;
    return Math.min(100, Math.max(0, ((baseline - current) / range) * 100));
  }

  if (goodDirection === 'up') {
    const range = best - baseline;
    if (range === 0) return 0;
    return Math.min(100, Math.max(0, ((current - baseline) / range) * 100));
  }

  const range = Math.abs(best - baseline);
  return Math.min(100, Math.max(0, (Math.abs(current) / range) * 100));
}

/** Design §4 benchmarkGauge: benchmark{best,baseline,current}, trend. Bar fill = theme primary. */
export default function BenchmarkGaugeRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const current = data.benchmark?.current ?? Number(data.value) ?? 0;
  const displayValue = formatMetricValue(current, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);
  const hasBenchmark = data.benchmark != null;
  const best = data.benchmark?.best;
  const baseline = data.benchmark?.baseline;
  const progress = hasBenchmark
    ? resolveBenchmarkProgress(current, best, baseline, config.goodDirection)
    : 0;
  const rangeLabels = resolveRangeLabels(best, baseline, data.format ?? config.format);

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            lineHeight: 1.4,
            minHeight: 40,
            mb: 1,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.15 }}>
          {displayValue}
        </Typography>
      </Box>
      <Box sx={{ mt: 'auto' }}>
        {hasBenchmark && rangeLabels ? (
          <BenchmarkBar value={progress} rangeLabels={rangeLabels} />
        ) : null}
        {data.trend ? (
          <Box sx={{ mt: hasBenchmark ? 1 : 0 }}>
            <TrendBadge trend={data.trend} />
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
}
